// src/auth/authNavigation.js
// Centralized helpers for auth-route redirects.
// Keeps /login, /register, /enter-code and protected routes from fighting each other.

export const LOOP_AUTH_ROUTES = new Set([
  "/login",
  "/register",
  "/complete-profile",
  "/signup",
  "/enter-code",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
]);

export function normalizePathOnly(value) {
  try {
    const raw = String(value || "").trim();
    if (!raw) return "/";

    // Never allow external redirects after auth.
    if (/^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.startsWith("//")) return "/";

    const url = raw.startsWith("/")
      ? new URL(raw, "https://loop.local")
      : new URL(`/${raw}`, "https://loop.local");

    // URL parsing treats backslashes as slashes; reject escaped authority paths.
    if (url.origin !== "https://loop.local" || /[\\\u0000-\u001f\u007f]/.test(raw)) return "/";

    return `${url.pathname || "/"}${url.search || ""}${url.hash || ""}`;
  } catch (_) {
    return "/";
  }
}

export function isAuthRoutePath(value) {
  try {
    const path = normalizePathOnly(value).split("?")[0].split("#")[0];
    return LOOP_AUTH_ROUTES.has(path);
  } catch (_) {
    return false;
  }
}

export function getSafeAuthRedirectPath(value, fallback = "/") {
  const safeFallback = normalizePathOnly(fallback || "/");
  const next = normalizePathOnly(value || safeFallback);
  if (!next || isAuthRoutePath(next))
    return isAuthRoutePath(safeFallback) ? "/" : safeFallback;
  return next;
}

export function getLoginRedirectTarget(location, fallback = "/") {
  try {
    const stateFrom = location?.state?.from || "";
    if (stateFrom) return getSafeAuthRedirectPath(stateFrom, fallback);

    const params = new URLSearchParams(location?.search || "");
    const queryFrom =
      params.get("from") ||
      params.get("redirect") ||
      params.get("redirectTo") ||
      "";
    return getSafeAuthRedirectPath(queryFrom, fallback);
  } catch (_) {
    return getSafeAuthRedirectPath(fallback, "/");
  }
}

export function buildLoginState(from = "/") {
  return { from: getSafeAuthRedirectPath(from, "/") };
}

export function notifyAuthRouteReady(detail = {}) {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("loop-auth-route-ready", { detail }),
      );
    }
  } catch (_) {}
}

export async function schedulePostAuthRedirect(
  navigate,
  to = "/",
  options = {},
) {
  const target = getSafeAuthRedirectPath(to, "/");
  const replace = options.replace !== false;
  const delayMs = Number.isFinite(Number(options.delayMs))
    ? Number(options.delayMs)
    : 0;

  notifyAuthRouteReady({ target });

  if (delayMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  } else {
    await Promise.resolve();
  }

  try {
    navigate(target, { replace });
  } catch (_) {
    try {
      window.location.assign(target);
    } catch (__) {}
  }
}
